'use client';

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { agriculturalAPI, chartsAPI } from '../services/api';
import {
  Search as SearchIcon,
  Filter,
  Download,
  Database,
  ChevronDown,
  X,
  FileText,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Maximize2,
  TrendingUp,
  PieChart,
  AreaChart,
  ChevronRight,

  Eye,
  ArrowLeft,
  ArrowRight,
  Activity,
  Table,
  FileSpreadsheet,
  Zap,
  Target,
  Save,
  Loader2,
  Lock,
  LogIn,
} from 'lucide-react';
import { RootState } from '../store/store';
import AdvancedFilterPanel from '../components/AdvancedFilterPanel';
import SaveChartModal from '../components/SaveChartModal';
import { analyzeSearchResults, FieldInfo } from '../services/dataAnalyzer';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart as RechartsAreaChart,
  Area,
  ScatterChart,
  Scatter,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Index {
  name: string;
  displayName: string;
  description: string;
  icon: string;
  documentCount: number;
  status: 'available' | 'not_available';
  health: string;
  size: string;
  lastModified: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    username: string;
    email: string;
  } | null;
  hasMetadata: boolean;
}

interface Field {
  name: string;
  type: string;
  displayName: string;
  searchable: boolean;
}

interface FilterValue {
  value: string;
  count: number;
}

interface SearchFilters {
  [key: string]: string;
}

interface SearchResult {
  _id: string;
  _score?: number;
  _highlights?: { [key: string]: string[] };
  [key: string]: any;
}

const Search: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  // Index selection
  const [selectedIndex, setSelectedIndex] = useState<string>('');
  const [indices, setIndices] = useState<Index[]>([]);
  const [indexSearchTerm, setIndexSearchTerm] = useState('');
  
  // Data and fields
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValue[] }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  
  // Save chart modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Chart configuration
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'area' | 'scatter' | 'radar' | 'combo' | 'table'>('bar');
  const [xAxisField, setXAxisField] = useState<string>('');
  const [yAxisField, setYAxisField] = useState<string>('');
  const [seriesField, setSeriesField] = useState<string>('');
  const [aggregation, setAggregation] = useState<'sum' | 'avg' | 'count'>('sum');
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartFilters, setChartFilters] = useState<{ [key: string]: any }>({});
  const [seriesToUnitMap, setSeriesToUnitMap] = useState<Record<string, string>>({}); // Unité par série (Item/Element)
  const [multipleChartsData, setMultipleChartsData] = useState<Array<{ title: string; data: any[]; filters: { [key: string]: any }; unit?: string }>>([]);

  // Dialog state for card details
  const [selectedCard, setSelectedCard] = useState<SearchResult | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState<number>(1);
  const steps = [
    { id: 1, title: 'Index', description: 'Select data source', icon: Database },
    { id: 2, title: 'Filters', description: 'Refine the data', icon: Filter },
    { id: 3, title: 'Configure & Preview', description: 'Chart configuration and live preview', icon: Eye },
  ];

  // Saved charts state for quick access
  const [savedCharts, setSavedCharts] = useState<any[]>([]);
  const [loadingSavedCharts, setLoadingSavedCharts] = useState(false);
  const [showQuickAccess, setShowQuickAccess] = useState(false);

  // Load saved charts for quick access
  const loadSavedCharts = async () => {
    try {
      setLoadingSavedCharts(true);
      const response = await chartsAPI.getCharts(true);
      if (response.data.success && response.data.data) {
        const charts = response.data.data || [];
        // Sort by most recent first
        const sortedCharts = charts.sort((a: any, b: any) => 
          new Date(b.metadata?.updatedAt || b.createdAt || 0).getTime() - 
          new Date(a.metadata?.updatedAt || a.createdAt || 0).getTime()
        );
        setSavedCharts(sortedCharts.slice(0, 6)); // Keep only 6 most recent
      }
    } catch (err: any) {
      console.error('Error loading saved charts:', err);
    } finally {
      setLoadingSavedCharts(false);
    }
  };

  // Load a saved chart into the search interface
  const loadChartIntoSearch = async (chartId: string) => {
    try {
      const response = await chartsAPI.getChart(chartId);
      if (response.data.success && response.data.data) {
        const chart = response.data.data;
        // Set the index
        if (chart.indexName) {
          setSelectedIndex(chart.indexName);
          // Wait for index to load, then set filters and chart config
          setTimeout(() => {
            if (chart.filters) {
              setChartFilters(chart.filters);
              setFilters(chart.filters);
            }
            if (chart.dataConfig) {
              if (chart.dataConfig.xAxis?.field) {
                setXAxisField(chart.dataConfig.xAxis.field);
              }
              if (chart.dataConfig.yAxis?.field) {
                setYAxisField(chart.dataConfig.yAxis.field);
              }
              if (chart.dataConfig.series?.length > 0 && chart.dataConfig.series[0]?.field) {
                setSeriesField(chart.dataConfig.series[0].field);
              }
              if (chart.type) {
                setChartType(chart.type as any);
              }
            }
            // Move to visualization step
            setCurrentStep(4);
            setShowQuickAccess(false);
          }, 500);
        }
      }
    } catch (err: any) {
      console.error('Error loading chart:', err);
      setError('Unable to load the saved chart');
    }
  };

  // Load indices on mount
  useEffect(() => {
    if (user) {
      loadIndices();
      loadSavedCharts();
    }
  }, [user]);

  // Load fields & data when index changes
  useEffect(() => {
    if (selectedIndex) {
      loadFields(selectedIndex);
      loadInitialData();
    }
  }, [selectedIndex]);

  // État pour suivre la détection automatique de série
  const [autoDetectedSeries, setAutoDetectedSeries] = useState<string | null>(null);
  const [multiValueFilterKey, setMultiValueFilterKey] = useState<string | null>(null);

  // Détecter automatiquement le champ série basé sur les filtres multi-sélectionnés
  useEffect(() => {
    // Analyser les filtres pour détecter les multi-sélections
    const multiValueFilters: { key: string; count: number }[] = [];
    Object.keys(chartFilters).forEach((filterKey) => {
      const filterValue = chartFilters[filterKey];
      // Vérifier si c'est un tableau avec plus d'une valeur
      if (Array.isArray(filterValue) && filterValue.length > 1) {
        multiValueFilters.push({ key: filterKey, count: filterValue.length });
      }
    });

    // Si on a un filtre multi-sélectionné et pas de série définie, suggérer automatiquement
    if (multiValueFilters.length > 0 && fields.length > 0) {
      // Exclure les champs temporels de la détection automatique (généralement utilisés sur l'axe X)
      // Cette exclusion est générale et fonctionne avec n'importe quel nom de champ temporel
      const filteredMultiValue = multiValueFilters.filter(f => {
        const lowerKey = f.key.toLowerCase();
        // Exclusion basée sur des patterns généraux, pas des noms spécifiques
        const temporalPatterns = ['year', 'date', 'month', 'quarter', 'week', 'day', 'time', 'period', 'année', 'mois', 'trimestre'];
        return !temporalPatterns.some(pattern => lowerKey.includes(pattern));
      });
      
      // Si on a des filtres non-Year, les utiliser, sinon utiliser tous les filtres
      const candidates = filteredMultiValue.length > 0 ? filteredMultiValue : multiValueFilters;
      
      // Prendre le filtre avec le plus de valeurs (priorité aux non-Year)
      const primaryFilter = candidates.sort((a, b) => b.count - a.count)[0];
      setMultiValueFilterKey(primaryFilter.key);

      // Trouver le champ correspondant au filtre multi-sélectionné
      const suggestedSeriesField = fields.find((f: FieldInfo) => {
        const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
        const normalizedFilter = normalize(primaryFilter.key);
        const normalizedField = normalize(f.name);
        const normalizedDisplay = normalize(f.displayName);
        
        return normalizedFilter === normalizedField || 
               normalizedField === normalizedFilter ||
               normalizedDisplay === normalizedFilter ||
               normalizedField.includes(normalizedFilter) || 
               normalizedFilter.includes(normalizedField) ||
               normalizedDisplay.includes(normalizedFilter) ||
               normalizedFilter.includes(normalizedDisplay);
      });

      if (suggestedSeriesField && suggestedSeriesField.canBeSeries) {
        setAutoDetectedSeries(suggestedSeriesField.name);
        // Auto-sélectionner le champ série seulement si l'utilisateur n'en a pas choisi un manuellement
        if (!seriesField) {
          setSeriesField(suggestedSeriesField.name);
        }
      } else {
        setAutoDetectedSeries(null);
      }
    } else {
      setAutoDetectedSeries(null);
      setMultiValueFilterKey(null);
    }
  }, [chartFilters, fields]);

  // Update chart preview when configuration changes — the aggregation is
  // server-side, so it does not depend on fetched results
  useEffect(() => {
    if (selectedIndex && xAxisField) {
      if (chartType === 'scatter') {
        // Pour scatter, utiliser les données brutes directement
        setChartData(results);
      } else {
        aggregateData();
      }
    }
  }, [chartType, xAxisField, yAxisField, seriesField, aggregation, results, chartFilters, selectedIndex]);

  const loadIndices = async () => {
    try {
      const response = await agriculturalAPI.getIndices();
      if (response.data.success) {
        setIndices(response.data.data);
        const availableIndex = response.data.data.find((index: Index) => index.status === 'available');
        if (availableIndex) {
          setSelectedIndex(availableIndex.name);
        }
      }
    } catch (err) {
      console.error('Error loading indices:', err);
    }
  };

  const loadFields = async (indexName: string) => {
    try {
      const response = await agriculturalAPI.getIndexFields(indexName);
      if (response.data.success) {
        // The API returns { data: { filterableFields: [...], ... } }
        const responseData = response.data.data || {};
        let fieldList = responseData.filterableFields || responseData.fields || [];
        
        // Ensure fieldList is an array
        if (!Array.isArray(fieldList)) {
          console.warn('Field list is not an array, converting:', fieldList);
          // If it's an object, try to extract values or keys
          if (typeof fieldList === 'object' && fieldList !== null) {
            // Try to get fields from common property names
            fieldList = fieldList.fields || fieldList.data || Object.values(fieldList) || [];
          } else {
            fieldList = [];
          }
        }
        // Convert to FieldInfo format
        const analyzedFields = fieldList.map((f: Field) => {
          const lowerName = f.name.toLowerCase();
          return {
            name: f.name,
            type: inferFieldType(f.type),
            displayName: f.displayName || f.name,
            canBeAxis: ['text', 'keyword', 'date'].includes(f.type) || lowerName === 'year',
            // Value est toujours métrique, Unit ne l'est jamais
            canBeMetric: (['integer', 'long', 'float', 'double'].includes(f.type) && lowerName !== 'unit') || lowerName === 'value',
            canBeSeries: ['text', 'keyword'].includes(f.type),
            canBeFilter: true,
          };
        });
        setFields(analyzedFields);
        
        // Auto-select first suitable fields
        const axisFields = analyzedFields.filter((f: FieldInfo) => f.canBeAxis);
        // Filtrer les métriques en excluant Unit et Year
        let metricFields = analyzedFields.filter((f: FieldInfo) => {
          const lowerName = f.name.toLowerCase();
          if (lowerName === 'value') return true; // Toujours inclure Value
          if (lowerName === 'unit') return false; // Exclure Unit
          if (lowerName === 'year' || lowerName.includes('year')) return false; // Exclure Year
          return f.canBeMetric;
        }).sort((a: FieldInfo, b: FieldInfo) => {
          // Prioriser Value en premier
          if (a.name.toLowerCase() === 'value') return -1;
          if (b.name.toLowerCase() === 'value') return 1;
          return 0;
        });
        
        if (axisFields.length > 0 && !xAxisField) {
          const yearField = axisFields.find((f: FieldInfo) => f.name.toLowerCase().includes('year'));
          setXAxisField(yearField ? yearField.name : axisFields[0].name);
        }
        if (metricFields.length > 0 && !yAxisField) {
          setYAxisField(metricFields[0].name);
        }
      }
    } catch (err) {
      console.error('Error loading fields:', err);
    }
  };

  const inferFieldType = (type: string): 'numeric' | 'categorical' | 'date' | 'text' | 'unknown' => {
    if (['integer', 'long', 'float', 'double'].includes(type)) return 'numeric';
    if (['date', 'date_nanos'].includes(type)) return 'date';
    if (['keyword'].includes(type)) return 'categorical';
    if (['text'].includes(type)) return 'text';
    return 'unknown';
  };

  const loadInitialData = async () => {
    if (!selectedIndex) return;
    try {
      setLoading(true);
      const response = await agriculturalAPI.searchIndex(selectedIndex, {
        page: 1,
        limit: 1000,
      });
      if (response.data.success) {
        setResults(response.data.data || []);
        setTotalResults(response.data.total || 0);
        // Analyze results to get better field info
        if (response.data.data && response.data.data.length > 0) {
          const analyzed = analyzeSearchResults(response.data.data);
          setFields(analyzed);
          
          // Auto-select fields
          const axisFields = analyzed.filter((f: FieldInfo) => f.canBeAxis);
          // Filtrer les métriques en excluant Unit et Year, prioriser Value
          let metricFields = analyzed.filter((f: FieldInfo) => {
            const lowerName = f.name.toLowerCase();
            // Toujours inclure Value même si canBeMetric est false
            if (lowerName === 'value') return true;
            // Exclure Unit qui est catégoriel
            if (lowerName === 'unit') return false;
            // Exclure Year qui est temporel
            if (lowerName === 'year' || lowerName.includes('year')) return false;
            return f.canBeMetric;
          }).sort((a: FieldInfo, b: FieldInfo) => {
            // TOUJOURS prioriser "value" en premier
            if (a.name.toLowerCase() === 'value') return -1;
            if (b.name.toLowerCase() === 'value') return 1;
            // Ensuite prioriser les champs contenant "value", "price", "amount", "quantity"
            const aIsValueLike = a.name.toLowerCase().includes('value') ||
                                 a.name.toLowerCase().includes('price') ||
                                 a.name.toLowerCase().includes('amount') ||
                                 a.name.toLowerCase().includes('quantity');
            const bIsValueLike = b.name.toLowerCase().includes('value') ||
                                 b.name.toLowerCase().includes('price') ||
                                 b.name.toLowerCase().includes('amount') ||
                                 b.name.toLowerCase().includes('quantity');
            if (aIsValueLike && !bIsValueLike) return -1;
            if (!aIsValueLike && bIsValueLike) return 1;
            return 0;
          });
          
          // S'assurer que Value est toujours dans la liste même si pas détecté
          if (!metricFields.find((f: FieldInfo) => f.name.toLowerCase() === 'value')) {
            const valueFieldFromAPI = analyzed.find((f: FieldInfo) => f.name.toLowerCase() === 'value');
            if (valueFieldFromAPI) {
              metricFields = [{
                ...valueFieldFromAPI,
                canBeMetric: true,
                type: 'numeric',
              }, ...metricFields];
              } else {
              // Créer Value si absent
              metricFields = [{
                name: 'value',
                type: 'numeric',
                displayName: 'Value',
                canBeAxis: false,
                canBeMetric: true,
                canBeSeries: false,
                canBeFilter: false,
              }, ...metricFields];
            }
          }
          
          if (axisFields.length > 0 && !xAxisField) {
            const yearField = axisFields.find((f: FieldInfo) => f.name.toLowerCase().includes('year'));
            setXAxisField(yearField ? yearField.name : axisFields[0].name);
          }
          if (metricFields.length > 0 && !yAxisField) {
            setYAxisField(metricFields[0].name);
          }
        }
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedIndex) return;
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: 1,
        limit: 1000,
      };
      
      if (searchTerm) {
        params.query = searchTerm;
      }
      
      // Add filters
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });
      
      // Add chart filters
      Object.keys(chartFilters).forEach((key) => {
        if (chartFilters[key]) {
          params[key] = chartFilters[key];
        }
      });

      const response = await agriculturalAPI.searchIndex(selectedIndex, params);
      if (response.data.success) {
        setResults(response.data.data || []);
        setTotalResults(response.data.total || 0);
      }
    } catch (err: any) {
      console.error('Error searching:', err);
      setError(err.response?.data?.message || 'Error searching');
    } finally {
      setLoading(false);
    }
  };

  const findRealFieldName = (fieldName: string, sampleRow: any): string | null => {
    if (!sampleRow) return null;
    if (sampleRow[fieldName] !== undefined) return fieldName;
    const lowerName = fieldName.toLowerCase();
    if (sampleRow[lowerName] !== undefined) return lowerName;
    const allKeys = Object.keys(sampleRow);
    const matchingKey = allKeys.find(k => k.toLowerCase() === lowerName);
    return matchingKey || null;
  };

  // Récupérer l'unité d'une ligne
  const getUnitFromRow = (row: any): string => {
    if (!row) return '';
    const k = Object.keys(row).find(kk => kk.toLowerCase() === 'unit');
    return k ? String(row[k] || '').trim() : '';
  };

  // Extraire l'unité des résultats - retourne l'unité SEULEMENT si TOUTES les lignes ont la même unité.
  // Si plusieurs unités différentes existent (ex: Wheat kg/ha vs Rice USD/tonne), retourne ''.
  const getUnitFromResults = (data: SearchResult[]): string => {
    if (!data || data.length === 0) return '';
    const unitKey = Object.keys(data[0]).find(k => k.toLowerCase() === 'unit');
    if (!unitKey) return '';
    const units = data.map(r => {
      const k = Object.keys(r).find(kk => kk.toLowerCase() === 'unit');
      return k ? String(r[k] || '').trim() : '';
    }).filter(u => u);
    if (units.length === 0) return '';
    const uniqueUnits = Array.from(new Set(units));
    if (uniqueUnits.length > 1) return ''; // Unités mixtes : ne pas afficher une unité unique
    return uniqueUnits[0];
  };

  // Fonction pour détecter tous les filtres multi-sélectionnés (pour séries combinées)
  const detectAllMultiValueFilters = (): Array<{ fieldName: string; filterKey: string; values: any[] }> => {
    const multiValueFilters: Array<{ fieldName: string; filterKey: string; values: any[] }> = [];
    
    console.log('🔍 Détection filtres - chartFilters:', chartFilters);
    console.log('🔍 Détection filtres - results disponibles:', results.length > 0);
    
    // Analyser tous les filtres pour trouver ceux avec plusieurs valeurs
    Object.entries(chartFilters).forEach(([filterKey, filterValue]) => {
      // Normaliser la valeur : si c'est un objet avec des valeurs, les extraire
      let normalizedValue = filterValue;
      if (filterValue && typeof filterValue === 'object' && !Array.isArray(filterValue)) {
        // Si c'est un objet, essayer d'extraire les valeurs
        if ('values' in filterValue && Array.isArray(filterValue.values)) {
          normalizedValue = filterValue.values;
        } else if ('value' in filterValue) {
          normalizedValue = [filterValue.value];
        } else {
          // Si c'est un objet simple, ignorer
          console.log(`⏭️ Filtre ignoré (objet non tableau): ${filterKey}`);
            return;
          }
      }
      
      console.log(`🔍 Vérification filtre: ${filterKey}`, {
        isArray: Array.isArray(normalizedValue),
        length: Array.isArray(normalizedValue) ? normalizedValue.length : 'N/A',
        value: Array.isArray(normalizedValue) ? normalizedValue.slice(0, 3) : normalizedValue,
        originalType: typeof filterValue
      });
      
      if (Array.isArray(normalizedValue) && normalizedValue.length > 1) {
        // Exclure les champs temporels (généralement sur l'axe X)
        const lowerKey = filterKey.toLowerCase();
        const temporalPatterns = ['year', 'date', 'month', 'quarter', 'week', 'day', 'time', 'period', 'année', 'mois', 'trimestre'];
        if (temporalPatterns.some(pattern => lowerKey.includes(pattern))) {
          console.log(`⏭️ Filtre temporel ignoré: ${filterKey}`);
          return; // Skip temporal fields
        }
        
        // Trouver le champ correspondant dans fields
        let matchingField = fields.find((f: FieldInfo) => {
          const normalizedFilter = filterKey.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
          const normalizedField = f.name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
          const normalizedDisplay = f.displayName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
          return normalizedFilter === normalizedField || 
                 normalizedField === normalizedFilter ||
                 normalizedDisplay === normalizedFilter ||
                 normalizedField.includes(normalizedFilter) || 
                 normalizedFilter.includes(normalizedField) ||
                 normalizedDisplay.includes(normalizedFilter) ||
                 normalizedFilter.includes(normalizedDisplay);
        });
        
        // Si pas trouvé dans fields, essayer de trouver dans les résultats
        if (!matchingField && results.length > 0) {
          const sampleRow = results[0];
          const possibleFieldName = findRealFieldName(filterKey, sampleRow);
          console.log(`🔍 Recherche dans résultats pour "${filterKey}":`, possibleFieldName);
          if (possibleFieldName) {
            // Créer un champ fictif pour permettre la série
            matchingField = {
              name: possibleFieldName,
              type: 'categorical',
              displayName: filterKey,
              canBeAxis: true,
              canBeMetric: false,
              canBeSeries: true,
              canBeFilter: true
            } as FieldInfo;
            console.log(`✅ Champ trouvé dans résultats: ${possibleFieldName}`);
          }
        }
        
        // Si toujours pas trouvé, utiliser le filterKey directement (fallback)
        if (!matchingField) {
          // Essayer de trouver dans les résultats avec le nom exact
          if (results.length > 0 && results[0][filterKey] !== undefined) {
            matchingField = {
              name: filterKey,
              type: 'categorical',
              displayName: filterKey,
              canBeAxis: true,
              canBeMetric: false,
              canBeSeries: true,
              canBeFilter: true
            } as FieldInfo;
            console.log(`✅ Utilisation directe du filterKey: ${filterKey}`);
          } else {
            // Essayer lowercase
            const lowerKey = filterKey.toLowerCase();
            if (results.length > 0 && results[0][lowerKey] !== undefined) {
              matchingField = {
                name: lowerKey,
                type: 'categorical',
                displayName: filterKey,
                canBeAxis: true,
                canBeMetric: false,
                canBeSeries: true,
                canBeFilter: true
              } as FieldInfo;
              console.log(`✅ Utilisation lowercase: ${lowerKey}`);
            }
          }
        }
        
        // Ajouter le filtre même si le champ n'est pas trouvé dans fields
        // On utilisera le filterKey ou le fieldName trouvé
        if (matchingField) {
          if (matchingField.canBeSeries) {
            console.log(`✅ Filtre ajouté comme série: ${filterKey} -> ${matchingField.name}`);
            multiValueFilters.push({
              fieldName: matchingField.name,
              filterKey: filterKey,
              values: normalizedValue
            });
          } else {
            // Même si canBeSeries est false, on l'ajoute quand même pour les graphiques multiples
            console.log(`✅ Filtre ajouté (canBeSeries=false mais utilisé quand même): ${filterKey} -> ${matchingField.name}`);
            multiValueFilters.push({
              fieldName: matchingField.name,
              filterKey: filterKey,
              values: normalizedValue
            });
          }
        } else {
          // Si le champ n'est pas trouvé, utiliser le filterKey directement
          console.log(`✅ Filtre ajouté (champ non trouvé, utilisation directe): ${filterKey}`);
          multiValueFilters.push({
            fieldName: filterKey,
            filterKey: filterKey,
            values: normalizedValue
          });
        }
      }
    });
    
    console.log(`🔍 Total filtres multi-sélectionnés détectés: ${multiValueFilters.length}`);
    return multiValueFilters;
  };

  // Fonction pour détecter automatiquement le champ série depuis les filtres (ancienne version pour compatibilité)
  const detectSeriesFromFilters = (): string | null => {
    const allFilters = detectAllMultiValueFilters();
    // Retourner le premier (pour compatibilité avec l'ancien code)
    return allFilters.length > 0 ? allFilters[0].fieldName : null;
  };

  // Helper function to convert code to name for chart series
  const convertCodeToName = (code: any, fieldName: string): string => {
    const mappings = buildCodeToNameMappings();
    const field = fields.find(f => f.name === fieldName || f.name.toLowerCase() === fieldName.toLowerCase());
    
    // Try to find mapping for this field
    let mapping: Map<any, string> | null = null;
    
    // Case 1: Field is a code field
    if (field?.isCode && field.relatedNameField && mappings[fieldName]) {
      mapping = mappings[fieldName];
    }
    // Case 2: Field is a name field, try to find related code field mapping
    else if (field?.isName && field.relatedCodeField && mappings[field.relatedCodeField]) {
      mapping = mappings[field.relatedCodeField];
    }
    // Case 3: Try common patterns
    else {
      const possibleCodeFields = [
        `${fieldName}Code`,
        `${fieldName}_code`,
        `${fieldName}_Code`,
      ];
      
      for (const codeField of possibleCodeFields) {
        if (mappings[codeField]) {
          mapping = mappings[codeField];
          break;
        }
      }
    }
    
    if (mapping) {
      const name = mapping.get(String(code)) || mapping.get(Number(code)) || mapping.get(code);
      if (name) {
        return name;
      }
    }
    
    // If no mapping found, return the code as string
    return String(code);
  };

  // Server-side aggregation — Elasticsearch aggregates the WHOLE index, so large
  // indices like producer_prices (~190k docs) produce correct charts.
  const aggregateData = async () => {
    if (!selectedIndex || !xAxisField || (chartType !== 'pie' && !yAxisField)) {
      setChartData([]);
      return;
    }

    try {
      // Series = multi-value filter fields (combined) or the explicit series field
      let allMultiValueFilters = detectAllMultiValueFilters();
      if (allMultiValueFilters.length === 0) {
        const temporalPatterns = ['year', 'date', 'month', 'quarter', 'week', 'day', 'time', 'period', 'année', 'mois', 'trimestre'];
        allMultiValueFilters = Object.entries(chartFilters)
          .filter(([key, val]) => Array.isArray(val) && val.length > 1 &&
            !temporalPatterns.some(p => key.toLowerCase().includes(p)))
          .map(([filterKey, val]) => ({ fieldName: filterKey, filterKey, values: val as any[] }));
      }

      const seriesFields = allMultiValueFilters.length > 0
        ? allMultiValueFilters.map(f => f.fieldName)
        : (seriesField ? [seriesField] : []);

      const response = await agriculturalAPI.aggregateIndex(selectedIndex, {
        xField: xAxisField,
        yField: chartType === 'pie' ? undefined : yAxisField,
        seriesFields,
        aggregation: chartType === 'pie' ? 'count' : aggregation,
        filters: chartFilters,
        limit: 30,
        seriesLimit: 12,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Aggregation failed');
      }

      const rows = (response.data.data || []).map((row: any) => {
        const { name, ...rest } = row;
        const displayName = convertCodeToName(name, xAxisField) || name;
        return {
          ...rest,
          name: displayName.length > 30 ? displayName.substring(0, 30) + '...' : displayName,
          fullName: displayName,
        };
      });

      // Unit for tooltip labels (best-effort from fetched rows)
      const rowUnit = getUnitFromResults(results);
      setSeriesToUnitMap(rowUnit ? { '': rowUnit } : {});
      setChartData(rows);
      console.log('📊 Server aggregation:', {
        index: selectedIndex, xField: xAxisField, yField: yAxisField,
        seriesFields, buckets: rows.length, totalDocs: response.data.totalDocs,
      });
    } catch (e: any) {
      console.error('Chart aggregation error:', e);
      setChartData([]);
    }
  };
  
  // Générer des graphiques multiples quand les résultats ou la configuration changent
  useEffect(() => {
    // Ne générer que si on est à l'étape 3 (configure & preview) et qu'on a la config nécessaire
    if (currentStep === 3 && xAxisField && yAxisField) {
      console.log('🔄 Génération des graphiques multiples déclenchée');
      generateMultipleCharts();
    } else {
      // Réinitialiser si on n'est pas à l'étape 3 ou si les données ne sont pas prêtes
      if (currentStep !== 3 || !xAxisField || !yAxisField) {
        setMultipleChartsData([]);
      }
    }
  }, [results, xAxisField, yAxisField, chartFilters, chartType, aggregation, currentStep]);

  // Fonction pour générer plusieurs graphiques (un par combinaison)
  const generateMultipleCharts = async () => {
    if (!selectedIndex || !xAxisField || !yAxisField) {
      setMultipleChartsData([]);
      return;
    }

    const sampleRow = results[0] || {};
    const realXAxisField = findRealFieldName(xAxisField, sampleRow) || xAxisField;
    const realYAxisField = findRealFieldName(yAxisField, sampleRow) || yAxisField;

    // Détecter tous les filtres multi-sélectionnés
    const allMultiValueFilters = detectAllMultiValueFilters();
    
    console.log('🔍 Détection pour graphiques multiples:', {
      allMultiValueFiltersCount: allMultiValueFilters.length,
      filters: allMultiValueFilters.map(f => ({ key: f.filterKey, count: f.values.length }))
    });
    
    // Si on a moins de 2 filtres, ne pas créer de graphiques multiples
    if (allMultiValueFilters.length < 2) {
      console.log('⚠️ Moins de 2 filtres multi-sélectionnés, pas de graphiques multiples');
      setMultipleChartsData([]);
      return;
    }

    // Générer toutes les combinaisons
    const combinations: Array<{ [key: string]: any }> = [];
    
    // Fonction récursive pour générer toutes les combinaisons
    const generateCombinations = (filters: Array<{ fieldName: string; filterKey: string; values: any[] }>, index: number, current: { [key: string]: any }) => {
      if (index >= filters.length) {
        combinations.push({ ...current });
        return;
      }

      const filter = filters[index];
      filter.values.forEach(value => {
        generateCombinations(filters, index + 1, {
          ...current,
          [filter.filterKey]: value,
          [filter.fieldName]: value
        });
      });
    };

    generateCombinations(allMultiValueFilters, 0, {});

    console.log(`🔍 Génération de ${combinations.length} graphiques pour ${combinations.length} combinaisons`);
    console.log('🔍 Combinaisons générées:', combinations);
    console.log('🔍 Exemple de ligne de données:', results[0]);

    // Créer un graphique pour chaque combinaison — server-side aggregation per
    // combination so large indices (e.g. producer_prices) are covered fully.
    const chartsData = await Promise.all(combinations.map(async (combination) => {
      // Merge global filters with this combination's pinned values
      const mergedFilters = { ...chartFilters };
      allMultiValueFilters.forEach(({ fieldName, filterKey }) => {
        const v = combination[filterKey] !== undefined ? combination[filterKey] : combination[fieldName];
        mergedFilters[filterKey] = v;
        mergedFilters[fieldName] = v;
      });

      // Nettoyer les filtres : exclure les champs "Code" (Area Code, Item Code, etc.)
      const cleanedFilters: { [key: string]: any } = {};
      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (key.toLowerCase().includes('code') && key.toLowerCase() !== 'code') return;
        if (value === null || value === undefined) return;
        if (Array.isArray(value)) {
          const filtered = value.filter(v => v !== null && v !== undefined && String(v).trim() !== '');
          if (filtered.length > 0) {
            cleanedFilters[key] = filtered.length === 1 ? filtered[0] : filtered;
          }
        } else if (String(value).trim()) {
          cleanedFilters[key] = String(value).trim();
        }
      });

      const titleParts = allMultiValueFilters.map(({ filterKey, fieldName }) => {
        const value = combination[filterKey];
        const displayValue = convertCodeToName(value, fieldName || filterKey);
        const displayLabel = getFilterDisplayLabel(filterKey);
        return `${displayLabel}: ${displayValue}`;
      });
      const title = titleParts.join(' | ');

      try {
        const response = await agriculturalAPI.aggregateIndex(selectedIndex, {
          xField: realXAxisField,
          yField: realYAxisField,
          seriesFields: [],
          aggregation,
          filters: cleanedFilters,
          limit: 30,
        });

        const data = response.data.success ? (response.data.data || []).map((row: any) => {
          const { name, ...rest } = row;
          const displayName = convertCodeToName(name, realXAxisField) || name;
          return {
            ...rest,
            name: displayName.length > 30 ? displayName.substring(0, 30) + '...' : displayName,
            fullName: displayName,
          };
        }) : [];

        return { title, data, filters: cleanedFilters, unit: getUnitFromResults(results) };
      } catch (e) {
        console.error('Combination aggregation failed:', combination, e);
        return { title, data: [], filters: cleanedFilters, unit: undefined };
      }
    }));

    setMultipleChartsData(chartsData);
    console.log(`✅ ${chartsData.length} graphiques générés:`, chartsData.map(c => c.title));
  };

  // Fonction pour rendre un graphique unique (utilisée pour les graphiques multiples)
  const renderSingleChart = (data: any[], unit?: string) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No data for this combination</p>
          </div>
        </div>
      );
    }

    const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];
    const yAxisLabel = unit ? `Value (${unit})` : 'Value';
    const tooltipFormatter = (value: any) => value != null ? `${Number(value).toLocaleString()}${unit ? ` ${unit}` : ''}` : '';

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Bar dataKey="value" fill={COLORS[0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
            </RechartsAreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => {
                  const total = data.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
                  const percent = total > 0 ? ((entry.value || 0) / total) * 100 : 0;
                  return `${entry.name || ''}: ${percent.toFixed(0)}%`;
                }}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => value != null ? `${Number(value).toLocaleString()}${unit ? ` ${unit}` : ''}` : ''} />
            </RechartsPieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        // Pour scatter, utiliser les données directement
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} label={unit ? { value: `Value (${unit})`, angle: -90, position: 'insideLeft', style: { fontSize: 10 } } : undefined} />
              <Tooltip formatter={tooltipFormatter} />
              <Scatter dataKey="value" fill={COLORS[0]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'radar':
        // Transformer les données pour le radar
        const radarData = data.map((item: any) => ({
          subject: item.name,
          value: item.value
        }));
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis angle={90} tick={{ fontSize: 10 }} label={unit ? { value: unit, angle: 0 } : undefined} />
              <Radar name={unit ? `Value (${unit})` : 'Value'} dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
              <Tooltip formatter={tooltipFormatter} />
            </RadarChart>
          </ResponsiveContainer>
        );

      case 'combo':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={unit ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10 } } : undefined} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip formatter={tooltipFormatter} />
              <Bar yAxisId="left" dataKey="value" fill={COLORS[0]} />
              <Line yAxisId="right" type="monotone" dataKey="value" stroke={COLORS[1]} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        );

      case 'table':
        // Pour le tableau, afficher un résumé simple
        return (
          <div className="overflow-auto h-full">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-semibold">Name</th>
                  <th className="px-2 py-1 text-right font-semibold">Value</th>
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 10).map((item: any, idx: number) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="px-2 py-1">{item.name}</td>
                    <td className="px-2 py-1 text-right">{item.value?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 10 && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Showing 10 of {data.length} results
              </p>
            )}
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Chart type not supported for multiple charts</p>
          </div>
        );
    }
  };

  const renderChart = () => {
    const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];
    
    // Pour scatter, vérifier les données brutes
    if (chartType === 'scatter') {
      if (results.length === 0 || !xAxisField || !yAxisField) {
        return (
          <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
            <div className="text-center">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Select two metrics (X and Y) for the Scatter chart</p>
            </div>
          </div>
        );
      }
    } else if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Select fields to view the chart</p>
          </div>
        </div>
      );
    }

    // Détecter si on a des séries multiples (basé sur les données, pas seulement seriesField)
    const seriesKeys = chartData.length > 0 && chartData[0] 
      ? Object.keys(chartData[0]).filter(k => k !== 'name' && k !== 'fullName')
      : [];
    const hasMultipleSeries = seriesKeys.length > 1;
    
    console.log('🔍 Détection séries dans le rendu:', {
      chartDataLength: chartData.length,
      hasMultipleSeries,
      seriesKeys,
      firstItemKeys: chartData[0] ? Object.keys(chartData[0]) : [],
      sampleData: chartData.slice(0, 2)
    });
    
    // Utiliser seriesField explicite ou détecter depuis les données
    const effectiveSeriesField = seriesField || (hasMultipleSeries ? 'auto' : null);

    const currentUnit = getUnitFromResults(results);
    const hasMixedUnits = hasMultipleSeries && Object.keys(seriesToUnitMap).length > 1;
    const mainYAxisLabel = hasMixedUnits ? (yAxisField || 'Value') : (currentUnit ? `${yAxisField || 'Value'} (${currentUnit})` : (yAxisField || 'Value'));
    const mainTooltipFormatter = (value: any, name?: any) => {
      if (value == null) return '';
      const seriesKey = typeof name === 'string' ? name : String(name ?? '');
      const unit = (seriesKey && seriesToUnitMap[seriesKey]) || currentUnit;
      return `${Number(value).toLocaleString()}${unit ? ` ${unit}` : ''}`;
    };

    const getSeriesDisplayName = (key: string) => seriesToUnitMap[key] ? `${key} (${seriesToUnitMap[key]})` : key;

    switch (chartType) {
      case 'bar':
        const barDataKey = effectiveSeriesField || 'value';
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: mainYAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={mainTooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                Object.keys(chartData[0] || {}).filter(k => k !== 'name' && k !== 'fullName').map((key, index) => (
                  <Bar key={key} dataKey={key} fill={COLORS[index % COLORS.length]} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Bar dataKey={barDataKey} fill={COLORS[0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        const lineDataKey = effectiveSeriesField || 'value';
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: mainYAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={mainTooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                Object.keys(chartData[0] || {}).filter(k => k !== 'name' && k !== 'fullName').map((key, index) => (
                  <Line key={key} type="monotone" dataKey={key} stroke={COLORS[index % COLORS.length]} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Line type="monotone" dataKey={lineDataKey} stroke={COLORS[0]} />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        const areaDataKey = effectiveSeriesField || 'value';
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: mainYAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={mainTooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                Object.keys(chartData[0] || {}).filter(k => k !== 'name' && k !== 'fullName').map((key, index) => (
                  <Area key={key} type="monotone" dataKey={key} stroke={COLORS[index % COLORS.length]} fill={COLORS[index % COLORS.length]} fillOpacity={0.6} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Area type="monotone" dataKey={areaDataKey} stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
              )}
            </RechartsAreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => {
                  const total = chartData.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
                  const percent = total > 0 ? ((entry.value || 0) / total) * 100 : 0;
                  return `${entry.name || ''}: ${percent.toFixed(0)}%`;
                }}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => value != null ? `${Number(value).toLocaleString()}${currentUnit ? ` ${currentUnit}` : ''}` : ''} />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        if (!yAxisField) {
          return (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500">The Scatter chart requires two metrics (X and Y)</p>
            </div>
          );
        }
        // Pour scatter, utiliser les données brutes avec xAxisField et yAxisField comme valeurs numériques
        const scatterData = results.map((row: any) => {
          const realX = findRealFieldName(xAxisField, row);
          const realY = findRealFieldName(yAxisField, row);
          return {
            x: realX ? parseFloat(row[realX]) || 0 : 0,
            y: realY ? parseFloat(row[realY]) || 0 : 0,
            name: row[realX || xAxisField] || '',
          };
        }).filter((item: any) => !isNaN(item.x) && !isNaN(item.y));
        
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={scatterData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis 
                type="number" 
                dataKey="x" 
                name={xAxisField}
                tick={{ fontSize: 11 }} 
                label={{ value: xAxisField, position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name={yAxisField}
                tick={{ fontSize: 12 }} 
                label={{ value: currentUnit ? `${yAxisField} (${currentUnit})` : yAxisField, angle: -90, position: 'insideLeft' }}
              />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value: any) => value != null ? `${Number(value).toLocaleString()}${currentUnit ? ` ${currentUnit}` : ''}` : ''} />
              <Legend />
              <Scatter name={`${xAxisField} vs ${yAxisField}`} dataKey="y" fill={COLORS[0]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'radar':
        if (!yAxisField) {
          return (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500">The Radar chart requires a metric</p>
            </div>
          );
        }
        // Pour radar, on transforme les données différemment
        const radarData = chartData.map((item: any) => ({
          subject: item.name,
          value: item.value || 0,
        }));
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 'auto']} tick={{ fontSize: 10 }} />
              <Radar name={currentUnit ? `${yAxisField} (${currentUnit})` : yAxisField} dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
              <Legend />
              <Tooltip formatter={mainTooltipFormatter} />
            </RadarChart>
          </ResponsiveContainer>
        );

      case 'combo':
        const comboDataKey = seriesField || 'value';
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: mainYAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip formatter={mainTooltipFormatter} />
              <Legend />
              <Bar yAxisId="left" dataKey={comboDataKey} fill={COLORS[0]} />
              <Line yAxisId="right" type="monotone" dataKey={comboDataKey} stroke={COLORS[1]} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        );

      case 'table':
        // Utiliser les données brutes pour le tableau
        const tableData = results.length > 0 ? results : chartData;
        if (tableData.length === 0) {
          return (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500">No data to display</p>
            </div>
          );
        }

        // Calculer les statistiques sur la colonne Y si disponible
        let stats = null;
        if (yAxisField && results.length > 0) {
          const sampleRow = results[0];
          const realY = findRealFieldName(yAxisField, sampleRow);
          if (realY) {
            const numericValues = results
              .map((row: any) => parseFloat(row[realY]) || 0)
              .filter(v => !isNaN(v) && v !== 0);
            
            if (numericValues.length > 0) {
              const sorted = [...numericValues].sort((a, b) => a - b);
              stats = {
                count: numericValues.length,
                sum: numericValues.reduce((a, b) => a + b, 0),
                avg: numericValues.reduce((a, b) => a + b, 0) / numericValues.length,
                min: Math.min(...numericValues),
                max: Math.max(...numericValues),
                median: sorted[Math.floor(sorted.length / 2)],
              };
            }
          }
        }

        // Obtenir toutes les colonnes à afficher
        const allColumns = Object.keys(tableData[0] || {}).filter(k => !k.startsWith('_'));
        const displayColumns = allColumns.slice(0, 10); // Limiter à 10 colonnes pour la lisibilité

  return (
          <div className="h-full overflow-auto">
            {/* Statistiques */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="text-xs text-gray-600 mb-1">Total</div>
                  <div className="text-lg font-bold text-blue-700">{stats.count}</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="text-xs text-gray-600 mb-1">Somme</div>
                  <div className="text-lg font-bold text-green-700">{stats.sum.toLocaleString()}</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="text-xs text-gray-600 mb-1">Moyenne</div>
                  <div className="text-lg font-bold text-purple-700">{stats.avg.toFixed(2)}</div>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <div className="text-xs text-gray-600 mb-1">Min</div>
                  <div className="text-lg font-bold text-orange-700">{stats.min.toLocaleString()}</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <div className="text-xs text-gray-600 mb-1">Max</div>
                  <div className="text-lg font-bold text-red-700">{stats.max.toLocaleString()}</div>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                  <div className="text-xs text-gray-600 mb-1">Median</div>
                  <div className="text-lg font-bold text-indigo-700">{stats.median.toLocaleString()}</div>
                </div>
              </div>
            )}

            {/* Tableau */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      {displayColumns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableData.slice(0, 100).map((row: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {displayColumns.map((col) => {
                          const value = row[col];
                          const displayValue = value !== null && value !== undefined 
                            ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                            : 'N/A';
                          const truncated = displayValue.length > 50 
                            ? displayValue.substring(0, 50) + '...' 
                            : displayValue;
                          
                          return (
                            <td
                              key={col}
                              className="px-4 py-3 text-sm text-gray-900 max-w-xs"
                              title={displayValue}
                            >
                              <div className="truncate">{truncated}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
        </div>
              {tableData.length > 100 && (
                <div className="px-4 py-3 bg-gray-100 text-center text-sm text-gray-600">
                  Showing 100 of {tableData.length.toLocaleString()} results
                </div>
              )}
        </div>
      </div>
        );

      default:
        return null;
    }
  };

  const getIndexIcon = (icon: string) => {
    return Database;
  };

  const exportToCSV = () => {
    if (chartData.length === 0 && results.length === 0) return;
    
    const dataToExport = chartType === 'table' ? chartData : results;
    if (dataToExport.length === 0) return;

    // Créer les en-têtes
    const headers = Object.keys(dataToExport[0]).filter(k => !k.startsWith('_'));
    const csvHeaders = headers.join(',');
    
    // Créer les lignes
    const csvRows = dataToExport.map((row: any) => {
      return headers.map(header => {
        const value = row[header];
        // Échapper les virgules et guillemets
        if (value === null || value === undefined) return '';
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(',');
    });
    
    // Combiner
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    
    // Télécharger
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedIndex}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedIndexData = indices.find(idx => idx.name === selectedIndex);
  const filteredIndices = indices.filter(idx => 
    !indexSearchTerm || 
    idx.displayName.toLowerCase().includes(indexSearchTerm.toLowerCase()) ||
    idx.name.toLowerCase().includes(indexSearchTerm.toLowerCase())
  );

  const activeFiltersCount = Object.keys(chartFilters).filter(k => {
    const value = chartFilters[k];
    return value !== null && value !== undefined && value !== '' && 
           (Array.isArray(value) ? value.length > 0 : true);
  }).length;

  // ---- Quick axis presets: one-click valid configurations built from detected fields ----
  const temporalField = fields.find(f =>
    f.name.toLowerCase().includes('year') || f.name.toLowerCase().includes('date') || f.type === 'date'
  );
  const valueLikeField = fields.find(f => f.name.toLowerCase() === 'value') ||
    fields.find(f => f.canBeMetric && ['value', 'price', 'amount', 'quantity'].some(k => f.name.toLowerCase().includes(k))) ||
    fields.find(f => f.canBeMetric && !['unit'].includes(f.name.toLowerCase()));
  const axisCandidates = fields.filter(f =>
    f.canBeAxis && f !== temporalField
  );

  const axisPresets: { label: string; description: string; x: string; y: string; type: string; icon: any }[] = [];
  if (temporalField && valueLikeField) {
    axisPresets.push({
      label: 'Trend over time',
      description: `${temporalField.displayName} → ${valueLikeField.displayName}`,
      x: temporalField.name, y: valueLikeField.name, type: 'line', icon: TrendingUp,
    });
  }
  axisCandidates.slice(0, 3).forEach((f) => {
    if (valueLikeField) {
      axisPresets.push({
        label: `Compare by ${f.displayName}`,
        description: `${f.displayName} → ${valueLikeField.displayName}`,
        x: f.name, y: valueLikeField.name, type: 'bar', icon: BarChart3,
      });
    }
  });

  const applyAxisPreset = (preset: { x: string; y: string; type: string }) => {
    setXAxisField(preset.x);
    setYAxisField(preset.y);
    if (preset.type) setChartType(preset.type as any);
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1: return selectedIndex !== '';
      case 2: return true; // Filtres optionnels
      case 3: return true; // Final step — configure & preview together
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length && canGoNext()) {
      if (currentStep === 2) {
        // Appliquer les filtres avant de passer à l'étape suivante
        handleSearch();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Build code-to-name mappings from search results
  const buildCodeToNameMappings = (): { [fieldName: string]: Map<any, string> } => {
    const mappings: { [fieldName: string]: Map<any, string> } = {};
    
    if (results.length === 0) return mappings;
    
    // Build mappings from search results (most reliable for code-to-name)
    fields.forEach((field) => {
      if (field.isCode && field.relatedNameField) {
        const codeField = field.name;
        const nameField = field.relatedNameField;
        const mapping = new Map<any, string>();
        
        // Build mapping from search results
        results.forEach((result) => {
          const codeValue = result[codeField];
          const nameValue = result[nameField];
          
          if (codeValue !== undefined && codeValue !== null && nameValue !== undefined && nameValue !== null) {
            // Handle both single values and arrays
            const codeValues = Array.isArray(codeValue) ? codeValue : [codeValue];
            const nameValues = Array.isArray(nameValue) ? nameValue : [nameValue];
            
            codeValues.forEach((code, index) => {
              if (code !== undefined && code !== null) {
                const name = nameValues[index] || nameValues[0] || String(code);
                // Map code to name (support both string and number codes)
                mapping.set(String(code), String(name));
                if (typeof code === 'number') {
                  mapping.set(code, String(name));
                }
              }
            });
          }
        });
        
        if (mapping.size > 0) {
          mappings[codeField] = mapping;
        }
      }
    });
    
    return mappings;
  };

  // Get display value for a filter (converting codes to names when possible)
  const getFilterDisplayValue = (filterKey: string, filterValue: any): string => {
    const mappings = buildCodeToNameMappings();
    const field = fields.find(f => f.name === filterKey || f.name.toLowerCase() === filterKey.toLowerCase());
    
    // Determine which mapping to use
    let mapping: Map<any, string> | null = null;
    let codeFieldName: string | null = null;
    
    // Case 1: Filter key is a code field (e.g., "areaCode")
    if (field?.isCode && field.relatedNameField && mappings[filterKey]) {
      mapping = mappings[filterKey];
      codeFieldName = filterKey;
    }
    // Case 2: Filter key is a name field but values might be codes (e.g., "area" with code values)
    else if (field?.isName && field.relatedCodeField && mappings[field.relatedCodeField]) {
      mapping = mappings[field.relatedCodeField];
      codeFieldName = field.relatedCodeField;
    }
    // Case 3: Try to find by pattern (e.g., filter key is "area" but we have "areaCode" mapping)
    else {
      // Try common patterns: if filterKey is "area", look for "areaCode" mapping
      const possibleCodeFields = [
        `${filterKey}Code`,
        `${filterKey}_code`,
        `${filterKey}_Code`,
      ];
      
      for (const codeField of possibleCodeFields) {
        if (mappings[codeField]) {
          mapping = mappings[codeField];
          codeFieldName = codeField;
          break;
        }
      }
    }
    
    // If it's an array, process each value
    if (Array.isArray(filterValue)) {
      if (filterValue.length === 0) return '';
      
      if (mapping && codeFieldName) {
        const names = filterValue
          .map(code => {
            // Try to find the name for this code
            const name = mapping!.get(String(code)) || mapping!.get(Number(code)) || mapping!.get(code);
            return name || String(code);
          })
          .filter(Boolean);
        
        if (names.length > 0) {
          // If we have many names, show count and first few names
          if (names.length > 3) {
            return `${names.length} selected (${names.slice(0, 2).join(", ")}, ...)`;
          }
          return names.join(', ');
        }
      }
      
      return `${filterValue.length} selected`;
    }
    
    // Single value
    if (mapping && codeFieldName) {
      const name = mapping.get(String(filterValue)) || mapping.get(Number(filterValue)) || mapping.get(filterValue);
      if (name) {
        return name;
      }
    }
    
    return String(filterValue);
  };

  // Get display label for a filter key (use name field label if it's a code field)
  const getFilterDisplayLabel = (filterKey: string): string => {
    const field = fields.find(f => f.name === filterKey || f.name.toLowerCase() === filterKey.toLowerCase());
    
    // If it's a code field, use the related name field's display name
    if (field?.isCode && field.relatedNameField) {
      const nameField = fields.find(f => f.name === field.relatedNameField);
      if (nameField) {
        return nameField.displayName || nameField.name;
      }
    }
    
    // Otherwise use the field's display name or the key itself
    return field?.displayName || filterKey;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div id="index-selection-section" className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow flex-shrink-0">
                <Database className="w-5 h-5 text-white" />
        </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Select an Index</h2>
                <p className="text-sm text-gray-600">Choose the data index you want to explore and visualize</p>
              </div>
            </div>
            <div className="relative mb-6">
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                value={indexSearchTerm}
                onChange={(e) => setIndexSearchTerm(e.target.value)}
                placeholder="Search indexes..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 placeholder-gray-500 shadow-sm transition-colors"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredIndices.map((index) => {
                const IndexIcon = getIndexIcon(index.icon);
                const isSelected = selectedIndex === index.name;
                const isAvailable = index.status === 'available';

                return (
                  <div
                    key={index.name}
                    onClick={() => isAvailable && setSelectedIndex(index.name)}
                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      isSelected
                        ? 'border-emerald-500 shadow-lg shadow-emerald-500/30 bg-emerald-50'
                        : isAvailable
                        ? 'border-gray-200 hover:border-emerald-300 hover:shadow-lg bg-white'
                        : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                          <IndexIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold truncate text-gray-900">{index.displayName}</h3>
                        <p className="text-xs truncate mt-1 text-gray-600">{index.description}</p>
                        <p className="text-xs mt-2 font-medium text-emerald-600">{index.documentCount.toLocaleString()} documents</p>
                    </div>
                    {isSelected && (
                        <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
        );

      case 2:
        return (
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow flex-shrink-0">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Advanced Filters</h2>
                <p className="text-sm text-gray-600">Refine your data by selecting specific criteria</p>
              </div>
      </div>

            {/* Aide visuelle pour la détection automatique */}
            <div className="mb-6 bg-emerald-50/80 border border-emerald-200 rounded-2xl p-4 shadow-lg">
              <div className="flex items-start space-x-3">
                <Sparkles className="h-5 w-5 mt-0.5 flex-shrink-0 text-emerald-600" />
              <div className="flex-1">
                  <h3 className="text-sm font-semibold mb-2 text-gray-900">💡 Tip: Automatically create multiple series</h3>
                  <div className="text-xs space-y-2 text-gray-700">
                    <p><strong className="text-gray-900">Concrete example:</strong> if you select <strong className="text-gray-900">several countries</strong> (e.g. Tunisia, Algeria, Morocco) in the \"Area\" filter:</p>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 ml-4">
                      <p className="font-medium mb-1 text-emerald-600">✅ What happens automatically:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>The system detects that you selected multiple values</li>
                        <li>It automatically sets \"Area\" as the series field</li>
                        <li>You get <strong>multiple lines</strong> in your chart (one per country)</li>
                      </ul>
                      <p className="mt-2 text-xs text-gray-600">📊 Result: a chart with 3 lines comparing the production of 3 countries over time</p>
                  </div>
                    <p className="text-emerald-600 font-medium">→ Works with any filter: Area, Element, Item, etc.</p>
                </div>
              </div>
              </div>
            </div>

            {/* Spinner global pour le chargement des filtres */}
            {loadingFilters && (
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl max-w-md mx-4">
                  <div className="flex flex-col items-center justify-center space-y-6">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-emerald-200 rounded-full"></div>
                      <div className="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin"></div>
                      <div className="absolute top-2 left-2 w-16 h-16 border-4 border-transparent border-r-emerald-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                      <div className="absolute top-4 left-4 w-12 h-12 border-4 border-transparent border-b-emerald-600 rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
                    </div>
                    <div className="text-center space-y-2">
                      <h3 className="text-xl font-bold text-gray-900">Detecting attributes</h3>
                      <p className="text-sm text-emerald-600 animate-pulse">Analysing data...</p>
                      <p className="text-xs text-gray-600">Loading available values for filtering</p>
                    </div>
                    <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full animate-shimmer bg-[length:200%_100%]"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <AdvancedFilterPanel
                indexName={selectedIndex}
                fields={fields}
                filters={chartFilters}
                onFiltersChange={(newFilters) => {
                  setChartFilters(newFilters);
                }}
                onApply={() => {
                  handleSearch();
                }}
                currentData={results}
                onLoadingChange={setLoadingFilters}
              />
                  </div>
                </div>
        );

      case 3:
                    return (
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow flex-shrink-0">
                <Eye className="w-5 h-5 text-white" />
                          </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Configure &amp; Preview</h2>
                <p className="text-sm text-gray-600">Configure your chart for <strong className="text-gray-900">{selectedIndexData?.displayName || selectedIndex}</strong> — the preview updates live below</p>
              </div>
            </div>
            {/* Spinner global pour le chargement des filtres */}
            {loadingFilters && (
              <div className="fixed inset-0 backdrop-blur-sm z-50 flex items-center justify-center bg-black/30">
                <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-2xl max-w-md mx-4">
                  <div className="flex flex-col items-center justify-center space-y-6">
                    {/* Spinner animé moderne */}
                    <div className="relative">
                      {/* Cercle externe rotatif */}
                      <div className="w-20 h-20 border-4 border-emerald-200 rounded-full"></div>
                      {/* Cercle interne rotatif */}
                      <div className="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin"></div>
                      {/* Cercle moyen */}
                      <div className="absolute top-2 left-2 w-16 h-16 border-4 border-transparent border-r-green-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                      {/* Cercle interne */}
                      <div className="absolute top-4 left-4 w-12 h-12 border-4 border-transparent border-b-teal-500 rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
                      {/* Point central */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-full animate-pulse"></div>
                    </div>
                    {/* Texte avec animation */}
                    <div className="text-center space-y-2">
                      <h3 className="text-xl font-bold text-gray-900">Detecting attributes</h3>
                      <p className="text-sm animate-pulse text-emerald-600">
                        Analysing data...
                      </p>
                      <p className="text-xs text-gray-600">
                        Loading available values for filtering
                      </p>
                    </div>
                    {/* Barre de progression animée */}
                    <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-full animate-shimmer bg-[length:200%_100%]"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
              {/* Chart Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-3 text-gray-900">Chart type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                    { type: 'bar', label: 'Bars', icon: BarChart3, description: 'Value comparison' },
                    { type: 'line', label: 'Lines', icon: TrendingUp, description: 'Time trends' },
                    { type: 'area', label: 'Area', icon: AreaChart, description: 'Cumulative evolution' },
                    { type: 'pie', label: 'Pie', icon: PieChart, description: 'Proportions' },
                    { type: 'scatter', label: 'Scatter', icon: Target, description: 'Correlations' },
                    { type: 'radar', label: 'Radar', icon: Activity, description: 'Multi‑metric' },
                    { type: 'combo', label: 'Combo', icon: Zap, description: 'Bars + Lines' },
                    { type: 'table', label: 'Table', icon: Table, description: 'Raw data' },
                  ].map(({ type, label, icon: Icon, description }) => (
                    <button
                      key={type}
                      onClick={() => setChartType(type as any)}
                      className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                        chartType === type
                          ? 'border-emerald-500 shadow-lg bg-emerald-50 text-emerald-600'
                          : 'border-gray-200 hover:border-emerald-300 text-gray-700 bg-white hover:bg-emerald-50/50'
                      }`}
                      title={description}
                    >
                      <Icon className="h-6 w-6 mb-2" />
                      <span className="text-sm font-medium">{label}</span>
                      <span className="text-xs mt-1 text-gray-500">{description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick setup — one-click valid configurations */}
              {axisPresets.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2 text-gray-900">
                    Quick setup
                    <span className="ml-2 text-xs font-normal text-gray-500">— one click fills everything below</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {axisPresets.map((preset) => (
                      <button
                        key={preset.label + preset.x}
                        onClick={() => applyAxisPreset(preset)}
                        className="flex items-center gap-3 px-4 py-3 border border-emerald-200 bg-emerald-50/60 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left"
                      >
                        <preset.icon className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900">{preset.label}</div>
                          <div className="text-xs text-gray-600 truncate">{preset.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <p className="text-xs text-gray-500 text-center">— or configure manually —</p>
                  </div>
                </div>
              )}

              {/* Axis Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900">
                    Group by (X axis) *
                        </label>
                              <select
                    value={xAxisField}
                    onChange={(e) => setXAxisField(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 transition-colors"
                    style={{ colorScheme: 'light' }}
                  >
                    <option value="" className="bg-white text-gray-900">Select a category field...</option>
                    {fields.filter(f => f.canBeAxis).map((field) => (
                      <option key={field.name} value={field.name} className="bg-white text-gray-900">
                        {field.displayName} ({field.type})
                                  </option>
                                ))}
                              </select>
                  <p className="text-xs text-gray-500 mt-1">
                    What each bar/point represents — e.g., a year, a country, a product.
                  </p>
                          </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900">
                    Measure (Y axis) *
                  </label>
                              <select
                    value={yAxisField}
                    onChange={(e) => setYAxisField(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 transition-colors"
                    style={{ colorScheme: 'light' }}
                  >
                    <option value="" className="bg-white text-gray-900">Select a numeric field...</option>
                    {fields
                      .filter((f: FieldInfo) => {
                        const lowerName = f.name.toLowerCase();
                        if (lowerName === 'value') return true;
                        if (lowerName === 'unit') return false;
                        if (lowerName === 'year' || lowerName.includes('year')) return false;
                        return f.canBeMetric;
                      })
                      .sort((a: FieldInfo, b: FieldInfo) => {
                        if (a.name.toLowerCase() === 'value') return -1;
                        if (b.name.toLowerCase() === 'value') return 1;
                        const aIsValueLike = a.name.toLowerCase().includes('value') ||
                                             a.name.toLowerCase().includes('price') ||
                                             a.name.toLowerCase().includes('amount') ||
                                             a.name.toLowerCase().includes('quantity');
                        const bIsValueLike = b.name.toLowerCase().includes('value') ||
                                             b.name.toLowerCase().includes('price') ||
                                             b.name.toLowerCase().includes('amount') ||
                                             b.name.toLowerCase().includes('quantity');
                        if (aIsValueLike && !bIsValueLike) return -1;
                        if (!aIsValueLike && bIsValueLike) return 1;
                        return 0;
                      })
                      .map((field) => (
                        <option key={field.name} value={field.name} className="bg-white text-gray-900">
                          {field.displayName} ({field.type})
                                  </option>
                                ))}
                              </select>
                  <p className="text-xs text-gray-500 mt-1">
                    The number to plot — e.g., production value, price.
                  </p>
                          </div>
              </div>

              {/* Plain-language summary of the current configuration */}
              {xAxisField && yAxisField && (
                <div className="mt-3 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-gray-800">
                  <span className="font-medium text-emerald-700">Your chart:</span>{' '}
                  a {chartType} chart of <strong>{aggregation === 'count' ? 'record count' : `${aggregation === 'avg' ? 'average' : 'total'} ${(fields.find(f => f.name === yAxisField)?.displayName || yAxisField).toLowerCase()}`}</strong>{' '}
                  grouped by <strong>{fields.find(f => f.name === xAxisField)?.displayName || xAxisField}</strong>
                  {seriesField && (
                    <> — one series per <strong>{fields.find(f => f.name === seriesField)?.displayName || seriesField}</strong></>
                  )}.
                </div>
              )}

              {(chartType !== 'pie' && chartType !== 'table' && chartType !== 'scatter') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Aggregation
                    </label>
                    <select
                      value={aggregation}
                      onChange={(e) => setAggregation(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900"
                      style={{ colorScheme: 'light' }}
                    >
                      <option value="sum" className="bg-white text-gray-900">Sum (total)</option>
                      <option value="avg" className="bg-white text-gray-900">Average</option>
                      <option value="count" className="bg-white text-gray-900">Count of records</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      How values are combined when several records share a group.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Split into series (optional)
                      {autoDetectedSeries && (
                        <span className="ml-2 text-xs text-emerald-600 font-normal">
                          (Auto-detected: {fields.find(f => f.name === autoDetectedSeries)?.displayName || autoDetectedSeries})
                        </span>
                      )}
                    </label>
                    <select
                      value={seriesField}
                      onChange={(e) => setSeriesField(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 ${
                        autoDetectedSeries && seriesField === autoDetectedSeries
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-300'
                      }`}
                      style={{ colorScheme: 'light' }}
                    >
                      <option value="" className="bg-white text-gray-900">None — single series</option>
                      {fields.filter(f => f.canBeSeries).map((field) => (
                        <option key={field.name} value={field.name} className="bg-white text-gray-900">
                          {field.displayName}
                          {field.name === autoDetectedSeries ? ' (auto-detected)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Draw one line/set of bars per value — e.g., one line per country.
                    </p>
                    {autoDetectedSeries && multiValueFilterKey && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                        <strong>💡 Auto-detection:</strong> you selected multiple values in the "{multiValueFilterKey}" filter.
                        The system set this field as the series to draw one line/set of bars per value.
                        {(() => {
                          // Détecter tous les filtres multi-sélectionnés
                          const allMultiFilters = Object.keys(chartFilters)
                            .filter(key => {
                              const value = chartFilters[key];
                              return Array.isArray(value) && value.length > 1;
                            })
                            .map(key => ({
                              key,
                              count: Array.isArray(chartFilters[key]) ? chartFilters[key].length : 0
                            }));
                          
                          if (allMultiFilters.length > 1) {
                            const otherFilters = allMultiFilters.filter(f => f.key !== multiValueFilterKey);
                            return (
                              <div className="mt-2 pt-2 border-t border-blue-300">
                                <p className="font-medium mb-1 text-gray-900">📊 Other multi-selected filters detected:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  {otherFilters.map((f, idx) => (
                                    <li key={idx}>
                                      <strong>{f.key}</strong>: {f.count} values 
                                      {f.key.toLowerCase().includes('year') || f.key.toLowerCase().includes('date') 
                                        ? ' (used on the X axis)' 
                                        : ' (can be used as the series if you prefer)'}
                                    </li>
                                  ))}
                                </ul>
                                <p className="mt-1 text-gray-600 italic">
                                  💡 You can change the series above to compare by another filter.
                                </p>
                      </div>
                    );
                          }
                          return null;
                        })()}
                </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Suggestions intelligentes basées sur les données */}
              {fields.length > 0 && (
                <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-4 shadow-lg">
                  <div className="flex items-start space-x-2">
                    <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">💡 Suggestions Intelligentes</h4>
                      <div className="text-xs text-gray-700 space-y-2">
                        {xAxisField && yAxisField ? (
                          <>
                            <p className="text-emerald-600 font-medium">✅ Valid configuration: {xAxisField} × {yAxisField}</p>
                            {/* Suggestions de type de graphique basées sur les données */}
                            {(() => {
                              const xField = fields.find(f => f.name === xAxisField);
                              const yField = fields.find(f => f.name === yAxisField);
                              const suggestions: string[] = [];
                              
                              if (xField?.type === 'date' || xField?.name.toLowerCase().includes('year')) {
                                suggestions.push('📈 Lines or Areas to visualize trends over time');
                              }
                              if (yField && chartData.length > 0) {
                                const uniqueX = new Set(chartData.map((d: any) => d.name)).size;
                                if (uniqueX <= 10) {
                                  suggestions.push('📊 Bars or Pie to compare a few categories');
                                } else if (uniqueX > 20) {
                                  suggestions.push('📈 Lines or Areas to visualize many categories');
                                }
                              }
                              if (xField && yField && xField.type === 'numeric' && yField.type === 'numeric') {
                                suggestions.push('🎯 Scatter to analyze correlations');
                              }
                              if (fields.filter(f => f.canBeMetric).length >= 3) {
                                suggestions.push('🕸️ Radar to compare several metrics at once');
                              }
                              
                              return suggestions.length > 0 ? (
                                <div className="mt-2 space-y-1">
                                  {suggestions.map((s, i) => (
                                    <p key={i}>{s}</p>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                        </>
                      ) : (
                        <>
                            {fields.filter(f => f.canBeAxis).length > 0 && (
                              <p>💡 For the X axis (category), try: <strong className="text-gray-900">{fields.filter(f => f.canBeAxis).slice(0, 3).map(f => f.displayName).join(', ')}</strong></p>
                            )}
                            {fields.filter(f => f.canBeMetric).length > 0 && (
                              <p>💡 For the Y axis (metric), try: <strong className="text-gray-900">{fields.filter(f => f.canBeMetric).slice(0, 3).map(f => f.displayName).join(', ')}</strong></p>
                            )}
                            {fields.filter(f => f.type === 'date' || f.name.toLowerCase().includes('year')).length > 0 && (
                              <p>📅 Temporal field detected: perfect for the X axis of time-series charts</p>
                            )}
                        </>
                      )}
                        {chartType === 'scatter' && !yAxisField && (
                          <p className="text-orange-400">⚠️ The Scatter chart requires two metrics (numeric X and Y)</p>
                        )}
                        {chartType === 'radar' && yAxisField && (
                          <p>ℹ️ The Radar chart shows one metric across multiple categories — ideal for comparing dimensions</p>
                        )}
                        {chartType === 'table' && (
                          <p>ℹ️ The table shows all raw data with descriptive statistics</p>
                        )}
                        {chartType === 'combo' && (
                          <p>ℹ️ The Combo chart combines bars and lines — useful for comparing two types of metrics</p>
                        )}
                        {autoDetectedSeries && multiValueFilterKey && (
                          <div className="mt-2 p-2 bg-[#1E90FF] bg-opacity-10 border border-[#1E90FF] rounded">
                            <p className="font-semibold text-[#1E90FF] mb-1">🎯 Automatic Series Detection</p>
                            {(() => {
                              const allMultiFilters = detectAllMultiValueFilters();
                              const hasCombinedSeries = allMultiFilters.length > 1;
                              
                              if (hasCombinedSeries) {
                                const totalCombinations = allMultiFilters.reduce((acc, f) => acc * f.values.length, 1);
                                return (
                                  <>
                                    <p className="text-gray-900">
                                      You selected <strong>multiple filters</strong> with multiple values:
                                    </p>
                                    <ul className="list-disc list-inside text-sm text-gray-700 mt-1 space-y-1">
                                      {allMultiFilters.map((f, idx) => (
                                        <li key={idx}>
                                          <strong>{f.filterKey}</strong> : {f.values.length} valeurs
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="text-gray-900 mt-2">
                                      The system will create <strong>{totalCombinations} combined series</strong> covering every combination.
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Example: 2 countries × 2 items = 4 series (Tunisia-Wheat, Tunisia-Corn, Algeria-Wheat, Algeria-Corn)
                                    </p>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <p className="text-gray-900">
                                      You selected <strong>{Array.isArray(chartFilters[multiValueFilterKey]) ? chartFilters[multiValueFilterKey].length : 1} values</strong> in the <strong>"{multiValueFilterKey}"</strong> filter.
                                      The system automatically set this field as the series, which will create <strong>multiple curves/lines</strong> in your chart (one per selected value).
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Example: if you select "Tunisia" and "Algeria", you will see 2 series — one per country.
                                    </p>
                                  </>
                                );
                              }
                            })()}
              </div>
            )}
          </div>
        </div>
          </div>
        </div>
      )}
        </div>

            {/* ─── Live preview (config + visualization merged into one step) ─── */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center space-x-2">
                <Eye className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">Live preview</h3>
                <span className="text-sm text-gray-500">— updates automatically as you change the configuration above</span>
              </div>
            </div>

            {xAxisField && (chartType === 'pie' || chartType === 'table' || yAxisField) ? (
              <>
            {/* Filtres Appliqués */}
            {(activeFiltersCount > 0 || searchTerm) && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-lg">
                <div className="flex items-start space-x-2 mb-3">
                  <Filter className="h-5 w-5 mt-0.5 text-emerald-600" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold mb-2 text-gray-900">Applied filters</h4>
                    <div className="flex flex-wrap gap-2 items-center">
                      {searchTerm && (
                        <span className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-xs font-medium">
                          Search: "{searchTerm}"
                        </span>
                      )}
                      {Object.keys(chartFilters).map((key) => {
                        const value = chartFilters[key];
                        if (!value || (Array.isArray(value) && value.length === 0)) return null;
                        const displayLabel = getFilterDisplayLabel(key);
                        const displayValue = getFilterDisplayValue(key, value);
                        return (
                          <span key={key} className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-xs font-medium">
                            {displayLabel}: {displayValue}
                          </span>
                        );
                      })}
                      {results.length > 0 && getUnitFromResults(results) && (
                    <span className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1" title="Measurement unit for values">
                          <span className="font-semibold">Unit:</span> {getUnitFromResults(results)}
                        </span>
                      )}
                      </div>
                      </div>
                    </div>
              </div>
            )}

            {/* Graphiques Multiples ou Graphique Unique */}
            {multipleChartsData.length > 0 ? (
              <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {multipleChartsData.length} Generated Charts
                      </h4>
                      <p className="text-xs text-gray-600">
                        One chart per selected filter combination
                      </p>
                          </div>
                  </div>
                    </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {multipleChartsData.map((chartInfo, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-2xl shadow-lg p-4 hover:border-emerald-300 transition-all">
                      <div className="mb-4">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1" title={chartInfo.title}>
                            {chartInfo.title}
                          </h3>
                          {chartInfo.unit && (
                            <span className="shrink-0 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-md text-xs font-medium" title="Measurement unit">
                              {chartInfo.unit}
                            </span>
                          )}
                        </div>
                        <div className="h-64">
                          {renderSingleChart(chartInfo.data, chartInfo.unit)}
                      </div>
                        {chartInfo.data.length === 0 && (
                          <p className="text-xs text-gray-500 text-center mt-2">
                            No data for this combination
                          </p>
                    )}
                </div>
              </div>
            ))}
          </div>
              </div>
            ) : (
              <div>
                {/* Graphique Unique */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-medium">
                    {chartType.charAt(0).toUpperCase() + chartType.slice(1)}
                  </span>
                  <span className="text-sm text-gray-600">
                    {xAxisField} × {yAxisField}
                  </span>
                  {results.length > 0 && getUnitFromResults(results) && (
                    <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-md text-xs font-medium" title="Measurement unit">
                      {getUnitFromResults(results)}
                    </span>
                  )}
                  {totalResults > 0 && (
                    <span className="text-sm text-gray-600">
                      ({totalResults.toLocaleString()} results)
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => aggregateData()}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                    title="Actualiser"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={exportToCSV}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                    title="Exporter en CSV"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => setIsSaveModalOpen(true)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all flex items-center space-x-2"
                    title="Save this chart"
                  >
                    <Save className="h-4 w-4" />
                    <span className="text-sm font-medium">Save chart</span>
                  </button>
                </div>
              </div>
              <div className="h-96 border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      <span>{error}</span>
        </div>
        </div>
                ) : (
                  renderChart()
                )}
              </div>
            </div>
        </div>
      )}

            {/* Cartes de valeurs */}
            {results.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
            <div>
                    <h3 className="text-lg font-semibold text-gray-900">Processed Data</h3>
                    <p className="text-sm text-gray-600">
                      Filtered and aggregated values ({results.length.toLocaleString()}{totalResults > 0 ? ` sur ${totalResults.toLocaleString()}` : ''})
                    </p>
            </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {results.slice(0, 20).map((result, index) => {
                    // Afficher seulement les 4 premiers champs dans la carte
                    const previewFields = Object.keys(result)
                      .filter(key => !key.startsWith('_'))
                      .slice(0, 4);
                    const allFields = Object.keys(result)
                      .filter(key => !key.startsWith('_'));
                    const hasMoreFields = allFields.length > 4;

                    return (
                      <div
                        key={result._id || index}
                        onClick={() => {
                          setSelectedCard(result);
                          setIsDialogOpen(true);
                        }}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all bg-white cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="p-1.5 bg-[#1E90FF] bg-opacity-10 rounded">
                              <Database className="h-4 w-4 text-[#1E90FF]" />
          </div>
                            <span className="text-xs font-semibold text-gray-700">#{index + 1}</span>
        </div>
                          {result._score && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Score: {result._score.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          {previewFields.map((field) => {
                            const value = result[field];
                            const displayValue = value !== null && value !== undefined 
                              ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                              : 'N/A';
                            const truncatedValue = displayValue.length > 50 
                              ? displayValue.substring(0, 50) + '...' 
                              : displayValue;

                            return (
                              <div key={field} className="border-t border-gray-100 pt-2 first:border-t-0">
                                <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                                  {field}
            </div>
                                <div className="text-sm text-gray-900 break-words">
                                  {truncatedValue}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {hasMoreFields && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-[#1E90FF] px-3 py-1.5 bg-blue-50 rounded-md">
                                +{allFields.length - 4} autres champs
                              </span>
                              <span className="text-xs text-gray-500">Cliquer pour voir tout</span>
              </div>
            </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {results.length > 20 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">
                      Showing 20 of {results.length.toLocaleString()} results
                    </p>
                  </div>
                )}
              </div>
            )}

            {results.length === 0 && !loading && (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-12 text-center">
                <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No data available</h3>
                <p className="text-gray-600 mb-4">
                  Apply filters in step 2 to see results here
                </p>
              <button
                  onClick={() => setCurrentStep(2)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
              >
                  Go to filters
              </button>
            </div>
            )}
              </>
            ) : (
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center">
                <Eye className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">
                  Choose <strong>Group by (X axis)</strong>
                  {chartType !== 'pie' && chartType !== 'table' && <> and <strong>Measure (Y axis)</strong></>}
                  {' '}above to see a live preview.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

                return (
    <div className="min-h-screen relative bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Animated Background - Nature inspired */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        {/* Decorative gradient circles */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-to-br from-emerald-200/20 via-green-200/20 to-teal-200/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-br from-teal-200/20 via-emerald-200/20 to-green-200/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
      </div>
      
      <div className="relative z-10">
        {/* Compact header — saved charts quick access (replaces the old tall hero) */}
        {user && (
          <div className="relative bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-b border-emerald-200/60 pt-20">
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 whitespace-nowrap" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    Your <span className="text-emerald-600">Saved Charts</span>
                  </h2>
                  <span className="text-sm text-gray-500 hidden sm:inline">
                    {savedCharts.length || 0} saved
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/my-charts')}
                    className="group px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow hover:shadow-emerald-500/40 flex items-center gap-2 text-sm font-semibold"
                  >
                    <Zap className="w-4 h-4" />
                    <span>Open my charts</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      setSelectedIndex('');
                      setFilters({});
                      setChartFilters({});
                      // Scroll to index selection section with offset for sticky navbar
                      setTimeout(() => {
                        const indexSection = document.getElementById('index-selection-section');
                        if (indexSection) {
                          const elementPosition = indexSection.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - 100;
                          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                        }
                      }, 150);
                    }}
                    className="px-4 py-2 bg-white border border-emerald-300 text-gray-900 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all text-sm font-semibold flex items-center gap-1"
                  >
                    <span>+</span>
                    <span>New chart</span>
                  </button>
                </div>
              </div>

              {loadingSavedCharts && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                </div>
              )}
            </div>
          </div>
        )}
        
        {!user ? (
          /* Mode invité - Aperçu verrouillé avec CTA Sign in */
          <section className="relative z-40 mt-16">
            <div className="max-w-4xl mx-auto px-6 py-16">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl mb-6">
                  <SearchIcon className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                  Data Explorer
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                  Explore millions of data points, apply advanced filters and create interactive visualizations. Log in to unlock full access.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-emerald-500/50 hover:from-emerald-600 hover:to-emerald-700 transition-all"
                >
                  <LogIn className="w-5 h-5" />
                  Sign in to explore
                </button>
              </div>

              {/* Les 3 étapes en cartes verrouillées */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 max-w-4xl mx-auto">
                {steps.map((step, index) => {
                  const StepIcon = step.icon;
                  return (
                    <div
                      key={step.id}
                      onClick={() => navigate('/login')}
                      className="relative group cursor-pointer bg-white/90 backdrop-blur-sm border-2 border-emerald-200 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="absolute top-3 right-3">
                        <Lock className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                          <StepIcon className="w-7 h-7 text-emerald-600" />
                        </div>
                        <span className="text-xs font-semibold text-emerald-600 mb-1">Step {step.id}</span>
                        <h3 className="font-bold text-gray-900">{step.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-center text-sm text-gray-500 mt-8">
                Index → Filters → Configure &amp; Preview
              </p>
            </div>
          </section>
        ) : (
          <>
        {/* Stepper Section - Smoothly connected to hero section */}
        <section className="relative z-40 bg-white/80 backdrop-blur-xl border-b border-emerald-200 shadow-lg transition-all duration-500">
          {/* Top gradient fade-in - creates seamless transition */}
          {user && (
            <>
              <div 
                className="absolute top-0 left-0 right-0 h-20 pointer-events-none"
                style={{
                  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.9) 100%)',
                }}
              />
              {/* Decorative line separator with glow */}
              <div 
                className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                style={{
                  background: 'linear-gradient(to right, transparent 0%, rgba(6, 182, 212, 0.3) 20%, rgba(6, 182, 212, 0.6) 50%, rgba(6, 182, 212, 0.3) 80%, transparent 100%)',
                  boxShadow: '0 1px 15px rgba(6, 182, 212, 0.4), 0 0 30px rgba(6, 182, 212, 0.2)',
                }}
              />
            </>
          )}
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-8">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                const isClickable = currentStep > step.id || (currentStep === step.id && canGoNext());
                
                return (
                  <div key={step.id} className="flex items-center">
                    {/* Step Circle */}
                    <div
                      onClick={() => isClickable && setCurrentStep(step.id)}
                      className={`
                        relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 cursor-pointer
                        ${isActive 
                          ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/50 scale-110' 
                          : isCompleted 
                            ? 'bg-emerald-500 text-white shadow-md' 
                            : 'bg-emerald-100 text-emerald-600 border-2 border-emerald-200'
                        }
                        ${isClickable ? 'hover:scale-105 hover:shadow-lg' : ''}
                      `}
                    >
                      <StepIcon className="w-6 h-6" />
                      {isCompleted && (
                        <CheckCircle className="absolute -bottom-1 -right-1 w-5 h-5 text-emerald-600 bg-white rounded-full" />
                      )}
                        </div>
                    
                    {/* Step Label */}
                    <div className="ml-3">
                      <div className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                        {step.title}
                      </div>
                    </div>

                    {/* Connector Line */}
                    {index < steps.length - 1 && (
                      <div className={`
                        mx-6 h-0.5 w-16 transition-all duration-300
                        ${isCompleted ? 'bg-emerald-500' : 'bg-emerald-200'}
                      `} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {renderStepContent()}
                      </div>

      {/* Navigation Footer - non fixe pour permettre le scroll */}
      <div className="bg-white/95 backdrop-blur-md border-t border-emerald-200 px-6 py-4 shadow-lg mt-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
                      <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
            <ArrowLeft className="h-4 w-4" />
            <span>Previous</span>
                      </button>
          <div className="text-sm text-gray-600">
            Step {currentStep} of {steps.length}
          </div>
                      {currentStep < steps.length && (
                      <button
            onClick={handleNext}
            disabled={!canGoNext()}
            className="flex items-center space-x-2 px-6 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                      >
            <span>Next</span>
            <ArrowRight className="h-4 w-4" />
                      </button>
                      )}
        </div>
      </div>
          </>
        )}

      {/* Dialog for Card Details */}
      {isDialogOpen && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Dialog Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#1E90FF] bg-opacity-10 rounded-lg">
                  <Database className="h-5 w-5 text-[#1E90FF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Document Details</h3>
                  <p className="text-sm text-gray-500">
                    {selectedCard._id && `ID: ${selectedCard._id}`}
                    {selectedCard._score && ` • Score: ${selectedCard._score.toFixed(2)}`}
                  </p>
                </div>
              </div>
                      <button
                onClick={() => {
                  setIsDialogOpen(false);
                  setSelectedCard(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
                      </button>
            </div>

            {/* Dialog Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {Object.keys(selectedCard)
                  .filter(key => !key.startsWith('_'))
                  .map((field) => {
                    const value = selectedCard[field];
                    const displayValue = value !== null && value !== undefined 
                      ? (typeof value === 'object' 
                          ? JSON.stringify(value, null, 2) 
                          : String(value))
                      : 'N/A';

                    return (
                      <div key={field} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors bg-gray-50/50">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          {field}
                        </div>
                        <div className="text-sm text-gray-900 break-words whitespace-pre-wrap">
                          {displayValue}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>

            {/* Dialog Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end flex-shrink-0">
              <button
                onClick={() => {
                  setIsDialogOpen(false);
                  setSelectedCard(null);
                }}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
              >
                Fermer
              </button>
            </div>
        </div>
      </div>
      )}

      {/* Save Chart Modal */}
      {currentStep === 3 && (
        <SaveChartModal
          isOpen={isSaveModalOpen}
          onClose={() => {
            setIsSaveModalOpen(false);
            setSaveSuccess(false);
          }}
          onSave={() => {
            setSaveSuccess(true);
          }}
          chartConfig={{
            type: chartType,
            indexName: selectedIndex,
            xAxisField: xAxisField,
            yAxisField: yAxisField,
            seriesField: seriesField || undefined,
            aggregation: aggregation,
            filters: chartFilters,
            chartData: chartData,
            totalResults: totalResults,
            unit: getUnitFromResults(results),
            seriesToUnitMap: seriesToUnitMap,
          }}
          fields={fields}
          multipleCharts={multipleChartsData.length > 0 ? multipleChartsData : undefined}
        />
      )}

      {/* Success Toast Notification */}
      {saveSuccess && (
        <div className="fixed top-6 right-6 z-50 animate-slide-in">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center space-x-3 border border-emerald-400/30">
            <CheckCircle className="h-6 w-6" />
            <div>
              <div className="font-semibold">Chart saved!</div>
              <div className="text-sm text-emerald-100">Your configuration has been saved successfully</div>
            </div>
          </div>
        </div>
      )}
      </div>
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Search;
